import { EventBusFacade, EventDispatch, EventSubscription } from "remote-module-context";

import { Deferred } from "./util/deferred";

export class EventBus {
	facades: Record<string, EventBusFacade> = {};
	waiting: Record<string, Deferred> = {};

	private subscriptions: Record<string, EventSubscription<unknown>[]> = {};

	createFacade(identifier: string, loadModule: (identifier: string) => Promise<void>): EventBusFacade {
		if (!this.facades[identifier]) {
			this.waiting = {
				...this.waiting,
				[identifier]: new Deferred(),
			};

			this.facades[identifier] = {
				subscribe: (subscription) => this.subscribe(identifier, subscription),
				dispatch: (dispatch) => this.dispatch(identifier, loadModule, dispatch),
			};
		}

		return this.facades[identifier];
	}

	private async dispatch<T>(
		sourceChannel: string,
		loadModule: (identifier: string) => Promise<void>,
		dispatch: EventDispatch<T>
	): Promise<void> {
		if (dispatch.channel === "shell" && dispatch.event === "ready") {
			this.waiting[sourceChannel].resolve();
			return;
		}

		await loadModule(dispatch.channel);
		const ms = 5000;
		const timeout = setTimeout(() => {
			throw new Error(
				`[Event Dispatch Failure] Channel "${dispatch.channel}" failed to be ready in ${ms} milliseconds.`
			);
		}, ms);

		await this.waiting[dispatch.channel].promise;
		clearTimeout(timeout);

		if (!this.subscriptions[dispatch.channel]) {
			return;
		}

		for (const subscription of this.subscriptions[dispatch.channel]) {
			if (dispatch.event === subscription.event) {
				subscription.eventHandler(dispatch.payload);
			}
		}
	}

	private subscribe<T>(channel: string, subscription: EventSubscription<T>) {
		this.subscriptions = {
			...this.subscriptions,
			[channel]: this.subscriptions[channel] ? [...this.subscriptions[channel], subscription] : [subscription],
		};

		return () => this.unsubscribe(channel, subscription);
	}

	private unsubscribe<T>(channel: string, eventSubscription: EventSubscription<T>) {
		this.subscriptions[channel] = this.subscriptions[channel].filter(
			(subscription) => subscription !== eventSubscription
		);
	}
}
